import { useState } from 'react';
import { toast } from 'sonner';
import { ExternalLink } from 'lucide-react';
import { ApiError } from '../../../lib/api';
import {
  useCompleteExternalTraining,
  type TrainingPlayerView,
} from '../../../hooks/useTrainingPlayer';
import { Button } from '../../ui/Button';

/**
 * The learner surface for an external (redirect + self-certify) module. No
 * slides or questions: instructions, a button that opens the training in a new
 * tab, and a confirmation the learner ticks to self-certify. The confirm +
 * complete controls stay disabled until they have opened the link at least
 * once (a light honesty nudge). Completion records their attestation, which
 * finishes the task; the parent TrainingPlayer then swaps to the done card.
 */
export function ExternalModuleScreen({
  taskId,
  view,
}: {
  taskId: string;
  view: TrainingPlayerView;
}) {
  const complete = useCompleteExternalTraining(taskId);
  const [opened, setOpened] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const url = view.external_url ?? '';
  const instructions = view.external_instructions?.trim() ?? '';
  const confirmation =
    view.external_confirmation?.trim() || 'I confirm I have completed this training.';

  async function onComplete() {
    try {
      await complete.mutateAsync();
      toast.success('Training marked as complete');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not record completion');
    }
  }

  return (
    <div className="space-y-5">
      {instructions ? (
        <p className="whitespace-pre-line text-sm text-fg">{instructions}</p>
      ) : (
        <p className="text-sm text-fg-muted">
          Open the training below, complete it on that site, then confirm you have done so.
        </p>
      )}

      <div>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setOpened(true)}
          className="inline-flex items-center gap-2 rounded-token-sm bg-accent px-4 py-2.5 text-sm font-medium text-fg-on-accent shadow-token-accent transition hover:opacity-90"
        >
          <ExternalLink className="h-4 w-4" />
          Open training in a new tab
        </a>
        {!opened ? (
          <p className="mt-2 text-xs text-fg-dim">Open the training first, then confirm below.</p>
        ) : null}
      </div>

      <label
        className={`flex items-start gap-2.5 rounded-token-sm border p-3 text-sm transition ${
          opened
            ? 'border-line/10 bg-surface-2 text-fg'
            : 'cursor-not-allowed border-line/10 bg-surface-inset text-fg-dim'
        }`}
      >
        <input
          type="checkbox"
          className="mt-0.5"
          checked={confirmed}
          disabled={!opened}
          onChange={(e) => setConfirmed(e.target.checked)}
        />
        <span>{confirmation}</span>
      </label>

      <Button
        variant="primary"
        disabled={!opened || !confirmed}
        loading={complete.isPending}
        onClick={() => void onComplete()}
      >
        Mark as complete
      </Button>
    </div>
  );
}
